import { useQuery } from "@tanstack/react-query";
import { BarChart3, Filter } from "lucide-react";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  WorkflowStatsCards,
  ComplexityPieChart,
  ReworkBarChart,
  ExecutionModeChart,
  ConsoleErrorTrendChart,
  TestPassRateTrendChart,
  VisualTestingCard,
  BatchMetricsCard,
} from "@/components/issues/metrics";

interface WorkflowMetrics {
  sprintFilter: string | null;
  summary: {
    totalIssues: number;
    activeIssues: number;
    avgClassificationScore: number | null;
    avgReworkCount: number;
    avgTestPassRate: number | null;
  };
  complexityDistribution: {
    simple: number;
    complex: number;
    sprint: number;
    unknown: number;
  };
  reworkByCategory: Array<{
    category: string;
    categoryLabel: string;
    avgReworkCount: number;
    issueCount: number;
  }>;
  executionModeBreakdown: Array<{
    mode: string;
    modeLabel: string;
    count: number;
    avgTestPassRate: number | null;
  }>;
  weeklyTrends: Array<{
    weekStart: string;
    weekLabel: string;
    avgConsoleErrors: number | null;
    avgTestPassRate: number | null;
    issueCount: number;
  }>;
  visualTestingMetrics?: {
    totalScreenshots: number;
    issuesWithScreenshots: number;
    avgScreenshotsPerIssue: number;
  } | null;
  coverage?: {
    complexityScore: number;
    executionMode: number;
    consoleErrors: number;
    rework: number;
    total: number;
  };
}

interface Sprint {
  id: string;
  sprintNumber: string;
  sprintName: string;
}

export default function IssueMetrics() {
  const [selectedSprint, setSelectedSprint] = useState<string>("all");

  // Fetch available sprints (uses default queryFn with auth headers)
  const { data: sprints } = useQuery<Sprint[]>({
    queryKey: ["/api/project-manager/sprints"],
  });

  // Fetch metrics with optional sprint filter (uses default queryFn with auth headers)
  const { data: metrics, isLoading, isError } = useQuery<WorkflowMetrics>({
    queryKey: ["/api/crm/issues/metrics", {
      ...(selectedSprint !== "all" ? { sprintId: selectedSprint } : {}),
    }],
    staleTime: 5 * 60 * 1000,
  });

  if (isError) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <BarChart3 className="h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-semibold mb-2">Failed to Load Metrics</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            There was an error loading the workflow metrics. Please try again later.
          </p>
        </div>
      </Card>
    );
  }

  // Check if there's any meaningful data
  const hasData = metrics && (
    metrics.summary.totalIssues > 0 ||
    metrics.complexityDistribution.simple > 0 ||
    metrics.complexityDistribution.complex > 0
  );

  if (!isLoading && !hasData) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Metrics Data Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Workflow metrics are collected as issues are processed through the
            Builder Agent workflow. Metrics will appear here once issues have
            been analysed and resolved.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sprint Filter */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <div className="flex-1 grid gap-2">
            <Label htmlFor="sprint-filter" className="text-sm font-medium">
              Filter by Sprint
            </Label>
            <Select
              value={selectedSprint}
              onValueChange={setSelectedSprint}
            >
              <SelectTrigger id="sprint-filter" className="w-[280px]">
                <SelectValue placeholder="Select sprint..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sprints</SelectItem>
                {sprints?.map((sprint) => (
                  <SelectItem key={sprint.id} value={sprint.sprintNumber}>
                    {sprint.sprintNumber}: {sprint.sprintName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedSprint !== "all" && (
            <div className="text-sm text-muted-foreground">
              Showing data for <span className="font-medium text-foreground">{selectedSprint}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Summary Stats Cards */}
      <WorkflowStatsCards
        totalIssues={metrics?.summary.totalIssues ?? 0}
        activeIssues={metrics?.summary.activeIssues ?? 0}
        avgClassificationScore={metrics?.summary.avgClassificationScore ?? null}
        avgReworkCount={metrics?.summary.avgReworkCount ?? 0}
        avgTestPassRate={metrics?.summary.avgTestPassRate ?? null}
        isLoading={isLoading}
      />

      {/* Data Coverage Indicator */}
      {metrics?.coverage && (
        <Card className="p-4 bg-muted/30">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex-1">
              <span className="text-muted-foreground">Data Coverage:</span>
              <span className="ml-2 font-medium">
                {Math.round((metrics.coverage.complexityScore / metrics.coverage.total) * 100)}% of issues have classification metrics
              </span>
              <span className="ml-2 text-muted-foreground">
                ({metrics.coverage.complexityScore}/{metrics.coverage.total} issues)
              </span>
            </div>
            {metrics.coverage.consoleErrors > 0 && (
              <div className="text-muted-foreground">
                Console errors tracked: {metrics.coverage.consoleErrors} issues
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Row 1: Complexity Distribution + Rework by Category */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ComplexityPieChart
          data={metrics?.complexityDistribution ?? { simple: 0, complex: 0, sprint: 0, unknown: 0 }}
          isLoading={isLoading}
        />
        <ReworkBarChart
          data={metrics?.reworkByCategory ?? []}
          isLoading={isLoading}
        />
      </div>

      {/* Row 2: Execution Mode Comparison (full width) */}
      <ExecutionModeChart
        data={metrics?.executionModeBreakdown ?? []}
        isLoading={isLoading}
      />

      {/* Row 2.5: Visual Testing Metrics (conditionally rendered if data exists) */}
      <VisualTestingCard
        data={metrics?.visualTestingMetrics}
        isLoading={isLoading}
      />

      {/* Row 2.6: Batch Performance Metrics (ISSUE-144) */}
      <BatchMetricsCard />

      {/* Row 3: Trend Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ConsoleErrorTrendChart
          data={metrics?.weeklyTrends ?? []}
          isLoading={isLoading}
        />
        <TestPassRateTrendChart
          data={metrics?.weeklyTrends ?? []}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
